import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/client';

const BLANK = {
  staff_name: '', mobile_number: '', designation: 'Teacher', custom_designation: '',
  staff_type: 'teaching', assigned_standard: '', assigned_division: '', status: 'active',
};

const DESIGNATIONS = ['Teacher', 'Principal', 'Headmaster', 'Clerk', 'Accountant', 'Librarian', 'Peon', 'Other'];

function fileToUrl(filePath) {
  if (!filePath) return null;
  return `/uploads/staff/${String(filePath).split('/').pop()}`;
}

export default function StaffForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(BLANK);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/students', { params: { limit: 5000 } }).then(res => setStudents(res.data.students || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/staff/${id}`).then(res => {
        const s = res.data.staff;
        setForm({
          staff_name: s.staff_name || '', mobile_number: s.mobile_number || '',
          designation: s.designation || 'Teacher', custom_designation: s.custom_designation || '',
          staff_type: s.staff_type || 'teaching', assigned_standard: s.assigned_standard || '',
          assigned_division: s.assigned_division || '', status: s.status || 'active',
        });
        setPhotoUrl(fileToUrl(s.photo_path));
        setSignatureUrl(fileToUrl(s.signature_path));
      }).catch(() => setError('Could not load staff member'));
    }
  }, [id, isEdit]);

  function handleChange(field, value) {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Non-Teaching can never carry a class teacher assignment — cleared
      // immediately on switch, not just hidden, so a stale value can't sneak through.
      if (field === 'staff_type' && value !== 'teaching') {
        next.assigned_standard = '';
        next.assigned_division = '';
      }
      return next;
    });
  }

  const standardOptions = useMemo(() => {
    const vals = new Set(students.map(s => s.current_standard || s.admission_standard).filter(Boolean));
    return [...vals].sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0) || String(a).localeCompare(String(b)));
  }, [students]);
  const divisionOptions = useMemo(() => {
    const vals = new Set(students.map(s => s.current_division || s.admission_division).filter(Boolean));
    return [...vals].sort();
  }, [students]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.staff_name.trim()) { setError('Staff name is required'); return; }
    if (!/^[0-9+\-\s]{7,15}$/.test(form.mobile_number.trim())) { setError('A valid mobile number is required'); return; }

    setSaving(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => { if (value !== null && value !== undefined) data.append(key, value); });
      if (photoFile) data.append('photo', photoFile);
      if (signatureFile) data.append('signature', signatureFile);

      if (isEdit) await api.put(`/staff/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await api.post('/staff', data, { headers: { 'Content-Type': 'multipart/form-data' } });

      navigate('/staff');
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred while saving');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout role="schoolAdmin">
      <div className="page-header">
        <div><h1 className="page-title">{isEdit ? 'Edit Staff Member' : 'Add Staff Member'}</h1></div>
      </div>

      {error && <div style={{ background: '#FEE2E2', color: 'var(--danger)', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label required">Staff Name</label>
              <input type="text" className="form-control" value={form.staff_name} onChange={e => handleChange('staff_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label required">Mobile Number</label>
              <input type="text" className="form-control" value={form.mobile_number} onChange={e => handleChange('mobile_number', e.target.value)} placeholder="9876543210" />
            </div>
          </div>

          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label required">Designation</label>
              <select className="form-control" value={form.designation} onChange={e => handleChange('designation', e.target.value)}>
                {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            {form.designation === 'Other' && (
              <div className="form-group">
                <label className="form-label">Specify Designation</label>
                <input type="text" className="form-control" value={form.custom_designation} onChange={e => handleChange('custom_designation', e.target.value)} />
              </div>
            )}
          </div>

          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label required">Staff Type</label>
              <select className="form-control" value={form.staff_type} onChange={e => handleChange('staff_type', e.target.value)}>
                <option value="teaching">Teaching</option>
                <option value="non_teaching">Non-Teaching</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => handleChange('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {form.staff_type === 'teaching' && (
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Class Teacher — Standard</label>
                <select className="form-control" value={form.assigned_standard} onChange={e => handleChange('assigned_standard', e.target.value)}>
                  <option value="">-- Not a Class Teacher --</option>
                  {standardOptions.map(s => <option key={s} value={s}>Standard {s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Class Teacher — Division</label>
                <select className="form-control" value={form.assigned_division} onChange={e => handleChange('assigned_division', e.target.value)} disabled={!form.assigned_standard}>
                  <option value="">-- Select Division --</option>
                  {divisionOptions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="form-section">
          <div className="form-row form-row-2">
            <UploadBox label="Staff Photo" icon="fa-user" file={photoFile} existingUrl={photoUrl} onChange={setPhotoFile} />
            <UploadBox label="Staff Signature" icon="fa-signature" file={signatureFile} existingUrl={signatureUrl} onChange={setSignatureFile} />
          </div>
        </div>

        <div className="form-section" style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-outline" onClick={() => navigate('/staff')}><i className="fas fa-times"></i> Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}><i className="fas fa-save"></i> {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </form>
    </Layout>
  );
}

function UploadBox({ label, icon, file, existingUrl, onChange }) {
  const preview = file ? URL.createObjectURL(file) : existingUrl;
  const inputId = `staff-upload-${label.replace(/\s+/g, '-')}`;
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div
        style={{ width: '100%', height: 120, border: '2px dashed var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden' }}
        onClick={() => document.getElementById(inputId).click()}
      >
        {preview ? (
          <img src={preview} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : (
          <><i className={`fas ${icon}`} style={{ fontSize: 28, color: 'var(--text-secondary)', marginBottom: 6 }}></i><span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Upload</span></>
        )}
      </div>
      <input type="file" id={inputId} accept="image/*" style={{ display: 'none' }} onChange={e => onChange(e.target.files[0] || null)} />
      {preview && <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 6 }} onClick={() => document.getElementById(inputId).click()}><i className="fas fa-sync"></i> Replace</button>}
    </div>
  );
}
